// Engine — main-thread lifecycle manager
// Handles: GPU init, worker, networking, input, mouse look, debug overlay

import { KeyboardInput, MouseLook } from "./input.js";
import { DebugOverlay } from "./debug.js";
import { initGPU } from "./gpu.js";
import type { GPU as GPUCtx } from "./gpu.js";
import { NetClient } from "../net/client.js";
import type { ToWorker, ToMain } from "./messages.js";
import { TICK_MS } from "./tick.js";

export type CameraState = {
  yaw: number;   // float radians
  pitch: number;  // float radians
  localPlayerId: number;
};

/** Writable debug info — the game render function fills this in for the engine debug overlay. */
export type DebugInfo = {
  playerX?: number;
  playerY?: number;
  playerZ?: number;
  triCount?: number;
};

export interface RenderContext {
  serverName?: string;
  mapName?: string;
  mapCreator?: string;
  mapCode?: string;
  relayHttp?: string;
  gameConfig?: Record<string, unknown>;
}

export interface EngineConfig {
  canvas: HTMLCanvasElement;
  workerUrl: string;
  sceneJson?: string;          // optional: provided for singleplayer/editor, omitted when joining a server
  serverUrl?: string;          // relay WebSocket URL (includes server ID in path)
  relayHttp?: string;          // relay HTTP URL (for API calls like voting)
  createRenderer: (gpu: GPUCtx, sceneJson: string, context?: RenderContext) => (buffer: Int32Array, prevBuffer: Int32Array | null, alpha: number, meta: unknown, camera: CameraState, debugInfo: DebugInfo) => void;
  debug?: boolean;
  onRejected?: (reason: string) => void;  // called when server rejects connection before game starts
}

export class Engine {
  private worker: Worker | null = null;
  private net: NetClient | null = null;
  private keyboard: KeyboardInput | null = null;
  private mouse: MouseLook | null = null;
  private debug: DebugOverlay | null = null;
  private render: ((buffer: Int32Array, prevBuffer: Int32Array | null, alpha: number, meta: unknown, camera: CameraState, debugInfo: DebugInfo) => void) | null = null;
  private gpu: GPUCtx | null = null;
  private sceneJson: string | null = null;
  private gameConfig: Record<string, unknown> | undefined;
  private renderContext: RenderContext | undefined;
  private localPlayerId = 0;
  private playerCount = 1;
  private started = false;
  private rafId = 0;

  private latestBuffer: Int32Array | null = null;
  private prevBuffer: Int32Array | null = null;
  private stateArrivalTime = 0;
  private latestMeta: unknown = null;
  private latestSimMs = 0;
  private latestEntityCount = 0;
  private gpuMs: number | undefined;
  private gpuTimePending = false;

  constructor(private config: EngineConfig) {
    this.sceneJson = config.sceneJson ?? null;
  }

  async start(): Promise<void> {
    const gpu = await initGPU(this.config.canvas);
    this.gpu = gpu;

    if (this.config.debug) {
      this.debug = new DebugOverlay();
    }

    if (this.config.serverUrl) {
      this.connectToServer();
    } else if (this.sceneJson) {
      // Singleplayer / editor: scene already known
      this.render = this.config.createRenderer(this.gpu, this.sceneJson, this.renderContext);
      this.launch();
    }
  }

  destroy(): void {
    this.worker?.terminate();
    this.worker = null;
    this.keyboard?.destroy();
    this.keyboard = null;
    this.mouse?.destroy();
    this.mouse = null;
    this.debug?.destroy();
    this.debug = null;
    cancelAnimationFrame(this.rafId);
    this.started = false;
  }

  private send(msg: ToWorker): void {
    this.worker?.postMessage(msg);
  }

  private launch(): void {
    if (this.started || !this.sceneJson) return;
    this.started = true;

    this.worker = new Worker(this.config.workerUrl, { type: "module" });

    this.worker.onmessage = (e: MessageEvent<ToMain>) => {
      const msg = e.data;
      switch (msg.type) {
        case "state":
          this.prevBuffer = this.latestBuffer;
          this.latestBuffer = msg.buffer;
          this.stateArrivalTime = performance.now();
          this.latestMeta = msg.meta;
          this.latestSimMs = msg.simMs;
          this.latestEntityCount = msg.entityCount;
          break;
        case "send_input":
          this.net?.sendInput(msg.tick, msg.playerId, msg.input);
          break;
      }
    };

    this.send({ type: "init", playerCount: this.playerCount, localPlayerId: this.localPlayerId, sceneJson: this.sceneJson, gameConfig: this.gameConfig });

    // Mouse look
    this.mouse = new MouseLook(this.config.canvas);

    // Keyboard — inject yaw from mouse into every input sample
    this.keyboard = new KeyboardInput((input) => {
      input.yaw = this.mouse!.yawFixed();
      this.send({ type: "input", playerId: this.localPlayerId, input });
    });

    // Also send yaw updates on mouse move (even without key change)
    document.addEventListener("mousemove", () => {
      if (this.keyboard && this.mouse) {
        this.keyboard.yaw = this.mouse.yawFixed();
        const input = this.keyboard.sample();
        this.send({ type: "input", playerId: this.localPlayerId, input });
      }
    });

    // Render loop
    const renderLoop = (): void => {
      if (!this.started) return;

      if (this.latestBuffer && this.render) {
        const camera: CameraState = {
          yaw: this.mouse!.yaw,
          pitch: this.mouse!.pitch,
          localPlayerId: this.localPlayerId,
        };
        const alpha = Math.min(1, (performance.now() - this.stateArrivalTime) / TICK_MS);
        const dbg: DebugInfo = {};
        const t0 = performance.now();
        this.render(this.latestBuffer, this.prevBuffer, alpha, this.latestMeta, camera, dbg);
        const renderMs = performance.now() - t0;

        // GPU time: measure how long the GPU takes to finish this frame's work
        if (!this.gpuTimePending && this.gpu) {
          this.gpuTimePending = true;
          const gpuStart = performance.now();
          this.gpu.device.queue.onSubmittedWorkDone().then(() => {
            this.gpuMs = performance.now() - gpuStart;
            this.gpuTimePending = false;
          });
        }

        this.debug?.update(this.latestSimMs, renderMs, this.latestEntityCount, this.latestBuffer[0], dbg.playerY, this.gpuMs, dbg.playerX, dbg.playerZ, dbg.triCount);
      }

      this.rafId = requestAnimationFrame(renderLoop);
    };
    this.rafId = requestAnimationFrame(renderLoop);

    console.log(`Ironbolt started — player ${this.localPlayerId} of ${this.playerCount}`);
  }

  private connectToServer(): void {
    this.net = new NetClient(this.config.serverUrl!, {
      onWelcome: (pid, count, mapJson, server) => {
        this.localPlayerId = pid;
        this.playerCount = count;
        this.sceneJson = mapJson;
        this.gameConfig = server.gameConfig ?? undefined;
        this.renderContext = { serverName: server.name, mapName: server.mapName, mapCreator: server.mapCreator, mapCode: server.mapCode, relayHttp: this.config.relayHttp, gameConfig: server.gameConfig };
        // Create renderer now that we have the map
        this.render = this.config.createRenderer(this.gpu!, mapJson, this.renderContext);
        console.log(`Assigned player ${pid} (${count} connected)`);
      },
      onStart: (count) => {
        this.playerCount = count;
        this.launch();
      },
      onInput: (_tick, playerId, input) => {
        this.send({ type: "input", playerId, input });
      },
      onPlayerJoined: (playerId, count) => {
        console.log(`Player ${playerId} joined (${count} connected)`);
        if (this.started) this.send({ type: "peer_joined", peerId: playerId });
      },
      onPlayerLeft: (playerId, count) => {
        console.log(`Player ${playerId} left (${count} connected)`);
        this.send({ type: "peer_left", peerId: playerId });
      },
      onServerFull: () => {
        console.log("Server is full");
        this.config.onRejected?.("Server is full");
      },
      onServerClosed: (reason) => {
        console.log(`Server closed: ${reason}`);
        if (!this.started) this.config.onRejected?.(reason);
      },
      onError: () => {
        // No server — fall back to singleplayer if we have a scene
        if (this.sceneJson) {
          console.log("No server found — starting singleplayer");
          this.net = null;
          this.render = this.config.createRenderer(this.gpu!, this.sceneJson, this.renderContext);
          this.launch();
        }
      },
    });

    this.net.connect();

    // Timeout: if connected but game doesn't start within 5s, go singleplayer
    setTimeout(() => {
      if (!this.started && this.sceneJson) {
        console.log("Server timeout — starting singleplayer");
        this.net = null;
        this.render = this.config.createRenderer(this.gpu!, this.sceneJson, this.renderContext);
        this.launch();
      }
    }, 5000);
  }
}
